/**
 * Quelltext-Seite des Editors auf Basis von CodeMirror 6.
 *
 * Warum eine Fremd-Lib: Mehrfachcursor (Blockauswahl mit Alt/Mausrad-Taste)
 * und Faltung wie in VS Code sind auf einem `<textarea>` nicht ehrlich
 * hinzubekommen – jede Eigenbau-Ebene darüber driftet früher oder später vom
 * echten Cursor weg. CodeMirror macht beides nativ.
 *
 * Nach außen verhält sich der Editor wie ein `MdField` (siehe field.ts),
 * damit `keys.ts`, `format.ts` und `slash.ts` unverändert weiterarbeiten.
 */

import {
  EditorState,
  EditorSelection,
  Prec,
  RangeSetBuilder,
  StateEffect,
  StateField,
  type Extension,
} from "@codemirror/state";
import {
  Decoration,
  EditorView,
  ViewPlugin,
  WidgetType,
  crosshairCursor,
  drawSelection,
  dropCursor,
  keymap,
  placeholder,
  rectangularSelection,
  type DecorationSet,
  type ViewUpdate,
} from "@codemirror/view";
import { defaultKeymap, history, historyKeymap, indentMore, indentLess, undo, redo } from "@codemirror/commands";
import { markdownSearch } from "./searchPanel";
import { mathAssist } from "./mathAssist";
import {
  codeFolding,
  foldEffect,
  foldKeymap,
  foldService,
  foldedRanges,
  unfoldEffect,
} from "@codemirror/language";
import { foldRanges, scanFences, type FoldRange } from "./folding";
import { icon } from "@/lib/icons";
import { isHiddenRef, toggleHiddenRef } from "@/markdown/fileref";
import { tableAlignPlugin } from "./tableAlign";
import { findTreeAt } from "./treeDiagram";
import { tableWidgets } from "./tableWidget";
import { charWidthOf, type MdField } from "./field";
import { findTableAt } from "./tableEditor";
import { foldSnippet, loadFolds, saveFolds, type FoldMark } from "./foldPersist";

/** Dreieck: nach unten = aufgeklappt, per CSS zur Seite gedreht = zugeklappt. */
const TRIANGLE = `<svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true"><path d="M2 4.5h8L6 9z" fill="currentColor"/></svg>`;

// ---------------------------------------------------------------------------
// Faltbare Bereiche (aus folding.ts) für CodeMirror bereitstellen
// ---------------------------------------------------------------------------

interface DocInfo {
  /** Faltbereich je Startzeile (0-basiert). */
  byLine: Map<number, FoldRange>;
  /** Pro Zeile: steckt sie in einem Codeblock? */
  inCode: boolean[];
}

/**
 * Die Textanalyse läuft einmal pro Dokumentfassung, nicht einmal pro Zeile –
 * die Faltleiste fragt sonst für jede sichtbare Zeile erneut an.
 */
const docCache = new WeakMap<EditorState["doc"], DocInfo>();

function docInfo(state: EditorState): DocInfo {
  const cached = docCache.get(state.doc);
  if (cached) return cached;
  const lines = state.doc.toString().split("\n");
  const byLine = new Map<number, FoldRange>();
  for (const r of foldRanges(lines)) {
    const prev = byLine.get(r.from);
    if (!prev || r.to > prev.to) byLine.set(r.from, r);
  }
  const info: DocInfo = { byLine, inCode: scanFences(lines).inCode };
  docCache.set(state.doc, info);
  return info;
}

/** Zeichenbereich, den das Falten von Zeile `line0` verbirgt. */
function charRange(state: EditorState, line0: number): { from: number; to: number } | null {
  const r = docInfo(state).byLine.get(line0);
  if (!r) return null;
  const head = state.doc.line(line0 + 1);
  const last = state.doc.line(Math.min(r.to + 1, state.doc.lines));
  return last.to > head.to ? { from: head.to, to: last.to } : null;
}

const mdFoldService = foldService.of((state, lineStart) =>
  charRange(state, state.doc.lineAt(lineStart).number - 1)
);

/** Bereits gefalteter Bereich, der genau bei `from` beginnt (falls vorhanden). */
function foldedAt(state: EditorState, from: number): { from: number; to: number } | null {
  const hits: { from: number; to: number }[] = [];
  foldedRanges(state).between(from, from, (f, t) => {
    if (f === from && t > f) hits.push({ from: f, to: t });
  });
  return hits[0] ?? null;
}

/** Auf-/Zuklappen des Bereichs, der bei `line0` beginnt (0-basiert). */
function toggleFoldAt(view: EditorView, line0: number): void {
  const range = charRange(view.state, line0);
  if (!range) return;
  const open = foldedAt(view.state, range.from);
  view.dispatch({ effects: open ? unfoldEffect.of(open) : foldEffect.of(range) });
}

/**
 * Faltungen aufräumen, sobald am zugeklappten Bereich gearbeitet wird:
 *
 *   - Die Kopfzeile (Überschrift, `- Punkt`, `::: info`, ```` ``` ````) wird
 *     bearbeitet oder gelöscht -> sofort aufklappen. Sonst verschwände nur die
 *     Kopfzeile und der Rest bliebe unsichtbar hängen.
 *   - Der zugeklappte Inhalt ist weg -> der leere Faltbereich muss weg, sonst
 *     sähe die Zeile für immer „zugeklappt" aus.
 */
const foldGuards = EditorState.transactionExtender.of((tr) => {
  if (!tr.docChanged) return null;
  const effects: StateEffect<{ from: number; to: number }>[] = [];
  foldedRanges(tr.startState).between(0, tr.startState.doc.length, (from, to) => {
    const head = tr.startState.doc.lineAt(from);
    const nextFrom = tr.changes.mapPos(from, 1);
    const nextTo = tr.changes.mapPos(to, -1);
    if (nextTo <= nextFrom || tr.changes.touchesRange(head.from, from)) {
      effects.push(unfoldEffect.of({ from: nextFrom, to: nextTo }));
    }
  });
  return effects.length ? { effects } : null;
});

// ---------------------------------------------------------------------------
// Zugeklappte Zeile blau hinterlegen (wie in VS Code)
// ---------------------------------------------------------------------------

const foldedLine = Decoration.line({ class: "cm-md-foldedLine" });

function foldedLineDecos(view: EditorView): DecorationSet {
  const starts: number[] = [];
  foldedRanges(view.state).between(0, view.state.doc.length, (from, to) => {
    if (to <= from) return; // Rest einer gelöschten Faltung
    starts.push(view.state.doc.lineAt(from).from);
  });
  starts.sort((a, b) => a - b);
  const b = new RangeSetBuilder<Decoration>();
  let last = -1;
  for (const s of starts) {
    if (s === last) continue;
    b.add(s, s, foldedLine);
    last = s;
  }
  return b.finish();
}

const foldedLineHighlight = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = foldedLineDecos(view);
    }
    update(u: ViewUpdate) {
      this.decorations = foldedLineDecos(u.view);
    }
  },
  { decorations: (v) => v.decorations }
);

// ---------------------------------------------------------------------------
// Auszeichnungs-Sternchen klein und hochgestellt (wie in der Vorschau)
// ---------------------------------------------------------------------------

const starMark = Decoration.mark({ class: "cm-md-star" });
/** `* Punkt` am Zeilenanfang ist ein Aufzählungszeichen, keine Auszeichnung. */
const BULLET_STAR = /^[ \t]*\*[ \t]/;

function starDecos(view: EditorView): DecorationSet {
  const { inCode } = docInfo(view.state);
  const b = new RangeSetBuilder<Decoration>();
  let done = -1;
  for (const range of view.visibleRanges) {
    let pos = range.from;
    while (pos <= range.to) {
      const line = view.state.doc.lineAt(pos);
      pos = line.to + 1;
      if (line.from <= done) continue;
      done = line.from;
      if (inCode[line.number - 1]) continue;
      const bullet = BULLET_STAR.test(line.text) ? line.text.indexOf("*") : -1;
      for (let i = 0; i < line.text.length; i++) {
        if (line.text[i] !== "*" || i === bullet) continue;
        b.add(line.from + i, line.from + i + 1, starMark);
      }
    }
  }
  return b.finish();
}

const emphasisStars = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = starDecos(view);
    }
    update(u: ViewUpdate) {
      // Auch beim Falten ändert sich, welche Zeilen sichtbar sind – darum
      // ohne Bedingung neu aufbauen (nur der sichtbare Ausschnitt, billig).
      this.decorations = starDecos(u.view);
    }
  },
  { decorations: (v) => v.decorations }
);

// ---------------------------------------------------------------------------
// Zeile unter der Maus kennzeichnen (für den Tabellen-Knopf – die Faltdreiecke
// selbst brauchen das nicht mehr, die zeigen sich per CSS `:hover`, siehe
// Faltmarkierungen unten).
// ---------------------------------------------------------------------------

const setHoverLine = StateEffect.define<number | null>();

const hoverLineField = StateField.define<number | null>({
  create: () => null,
  update(value, tr) {
    for (const e of tr.effects) if (e.is(setHoverLine)) value = e.value;
    return value;
  },
});

const hoverLineTracker = ViewPlugin.fromClass(
  class {
    private line: number | null = null;
    private readonly move = (e: MouseEvent) => {
      const pos = this.view.posAtCoords({ x: e.clientX, y: e.clientY }, false);
      const from = this.view.state.doc.lineAt(pos).from;
      if (from === this.line) return;
      this.line = from;
      this.view.dispatch({ effects: setHoverLine.of(from) });
    };
    private readonly leave = () => {
      if (this.line == null) return;
      this.line = null;
      this.view.dispatch({ effects: setHoverLine.of(null) });
    };
    constructor(private readonly view: EditorView) {
      view.dom.addEventListener("mousemove", this.move);
      view.dom.addEventListener("mouseleave", this.leave);
    }
    destroy() {
      this.view.dom.removeEventListener("mousemove", this.move);
      this.view.dom.removeEventListener("mouseleave", this.leave);
    }
  }
);

// ---------------------------------------------------------------------------
// Verweis auf eine PDF-Seite als Knopf – auch im Quelltext
// ---------------------------------------------------------------------------

/** `[📄 Datei, S. 3](pdf:<id>:3)` – Beschriftung und Ziel. */
const PDF_LINK_RE = /\[([^\]\n]*)\]\((pdf:[^)\s]+)\)/g;

class PdfChip extends WidgetType {
  constructor(
    private readonly label: string,
    private readonly href: string,
    private readonly open: (href: string) => void
  ) {
    super();
  }
  eq(other: PdfChip): boolean {
    return other.label === this.label && other.href === this.href;
  }
  toDOM(): HTMLElement {
    const el = document.createElement("span");
    el.className = "cm-md-pdfChip";
    el.textContent = this.label;
    el.title = "Diese Seite im Arbeits-PDF öffnen";
    el.addEventListener("mousedown", (e) => e.preventDefault());
    el.addEventListener("click", (e) => {
      e.preventDefault();
      this.open(this.href);
    });
    return el;
  }
  ignoreEvent(): boolean {
    return true;
  }
}

/**
 * Zeichnet jeden PDF-Verweis als Knopf. Der Quelltext bleibt darunter
 * unverändert stehen – der Knopf lässt sich also wie Text markieren,
 * kopieren und löschen. Steht der Cursor in dem Verweis, tritt der Knopf
 * beiseite, damit man die Zeichen bearbeiten kann.
 */
function pdfChips(view: EditorView, open: (href: string) => void): DecorationSet {
  const b = new RangeSetBuilder<Decoration>();
  const cursor = view.state.selection.main;
  for (const { from, to } of view.visibleRanges) {
    const text = view.state.doc.sliceString(from, to);
    PDF_LINK_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = PDF_LINK_RE.exec(text))) {
      const start = from + m.index;
      const end = start + m[0].length;
      if (cursor.from <= end && cursor.to >= start) continue;
      b.add(start, end, Decoration.replace({ widget: new PdfChip(m[1], m[2], open) }));
    }
  }
  return b.finish();
}

function pdfChipPlugin(open: (href: string) => void) {
  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = pdfChips(view, open);
      }
      update(u: ViewUpdate) {
        this.decorations = pdfChips(u.view, open);
      }
    },
    { decorations: (v) => v.decorations }
  );
}

// ---------------------------------------------------------------------------
// Bild in Vorschau/Export ausblenden (Auge-Symbol links) – siehe fileref.ts.
// Für Fotos, die nur für einen selbst gedacht sind (z. B. ein Tafelbild),
// nicht aber für Vorschau/Notiz-Export/Hausaufgaben-Ansicht, die ein Lehrer
// sehen könnte. Der Quelltext bleibt in jedem Fall vollständig erhalten.
// ---------------------------------------------------------------------------

const IMAGE_REF_RE = /!\[[^\]\n]*\]\((pb:[^)\s]+)\)/g;

class ImageEyeWidget extends WidgetType {
  constructor(
    private readonly hidden: boolean,
    private readonly toggle: () => void
  ) {
    super();
  }
  eq(other: ImageEyeWidget): boolean {
    return other.hidden === this.hidden;
  }
  toDOM(): HTMLElement {
    const el = document.createElement("span");
    el.className = "cm-md-eyeToggle" + (this.hidden ? " is-hidden" : "");
    el.innerHTML = icon(this.hidden ? "eye-off" : "eye", 13);
    el.title = this.hidden
      ? "Bild ist in Vorschau/Export ausgeblendet – wieder einblenden"
      : "Bild in Vorschau/Export ausblenden (z. B. ein Tafelbild-Foto nur für dich selbst)";
    el.setAttribute("role", "button");
    el.addEventListener("mousedown", (e) => e.preventDefault());
    el.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggle();
    });
    return el;
  }
  ignoreEvent(): boolean {
    return true;
  }
}

function imageEyeDecos(view: EditorView): DecorationSet {
  const b = new RangeSetBuilder<Decoration>();
  for (const { from, to } of view.visibleRanges) {
    const text = view.state.doc.sliceString(from, to);
    IMAGE_REF_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = IMAGE_REF_RE.exec(text))) {
      const lineStart = from + m.index;
      const href = m[1];
      const hrefStart = lineStart + m[0].indexOf(href);
      const toggle = () => {
        view.dispatch({
          changes: { from: hrefStart, to: hrefStart + href.length, insert: toggleHiddenRef(href) },
        });
      };
      b.add(lineStart, lineStart, Decoration.widget({ widget: new ImageEyeWidget(isHiddenRef(href), toggle), side: -1 }));
    }
  }
  return b.finish();
}

const imageEyeToggle = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = imageEyeDecos(view);
    }
    update(u: ViewUpdate) {
      this.decorations = imageEyeDecos(u.view);
    }
  },
  { decorations: (v) => v.decorations }
);

// ---------------------------------------------------------------------------
// „Tabelle bearbeiten" direkt am Tabellen-Quelltext
// ---------------------------------------------------------------------------

class TableChip extends WidgetType {
  constructor(
    private readonly at: number,
    private readonly run: () => void
  ) {
    super();
  }
  eq(other: TableChip): boolean {
    return other.at === this.at;
  }
  toDOM(view: EditorView): HTMLElement {
    const el = document.createElement("button");
    el.type = "button";
    el.className = "cm-md-tableEditIcon";
    el.innerHTML = icon("table", 13);
    el.title = "Tabelle bearbeiten";
    el.setAttribute("aria-label", "Tabelle bearbeiten");
    // Ohne das verliert der Editor beim Klick den Fokus und damit die Stelle.
    el.addEventListener("mousedown", (e) => e.preventDefault());
    el.addEventListener("click", (e) => {
      e.preventDefault();
      // Bei reinem Hover steht der Cursor woanders – erst in DIESE Tabelle
      // setzen, sonst öffnet sich ein leerer Editor statt dieser Tabelle.
      view.dispatch({ selection: { anchor: this.at } });
      this.run();
    });
    return el;
  }
  ignoreEvent(): boolean {
    return true;
  }
}

/**
 * Zeigt am Kopf einer Tabelle ein Symbol im Seitenrand, sobald der Cursor
 * darin steht oder die Maus darüber fährt – damit man den visuellen Tabellen-Editor findet,
 * ohne das Symbol in der Werkzeugleiste zu kennen.
 */
function tableChips(view: EditorView, run: () => void): DecorationSet {
  const positions = [view.state.selection.main.head];
  const hovered = view.state.field(hoverLineField, false);
  if (hovered != null) positions.push(hovered);

  // Am ANFANG der Kopfzeile (Symbol im Seitenrand, wie die Faltdreiecke): am
  // Zeilenende landete der Knopf bei umbrochenen Zeilen mitten im Text.
  const heads = new Set<number>();
  for (const pos of positions) {
    const line = view.state.doc.lineAt(pos);
    if (!/^\s*\|/.test(line.text)) continue;
    const block = findTableAt(view.state.doc.toString(), pos);
    if (block) heads.add(view.state.doc.lineAt(block.start).from);
  }

  const b = new RangeSetBuilder<Decoration>();
  for (const at of [...heads].sort((x, y) => x - y)) {
    b.add(at, at, Decoration.widget({ widget: new TableChip(at, run), side: -1 }));
  }
  return b.finish();
}

function tableChipPlugin(run: () => void) {
  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = tableChips(view, run);
      }
      update(u: ViewUpdate) {
        this.decorations = tableChips(u.view, run);
      }
    },
    { decorations: (v) => v.decorations }
  );
}

// ---------------------------------------------------------------------------
// „Baumdiagramm bearbeiten" im Mermaid-Block eines Baumdiagramms: dasselbe
// Randsymbol wie bei Tabellen, an der Zeile mit dem öffnenden Zaun.
// ---------------------------------------------------------------------------

class TreeChip extends WidgetType {
  constructor(
    private readonly at: number,
    private readonly run: () => void
  ) {
    super();
  }
  eq(other: TreeChip): boolean {
    return other.at === this.at;
  }
  toDOM(view: EditorView): HTMLElement {
    const el = document.createElement("button");
    el.type = "button";
    el.className = "cm-md-tableEditIcon";
    el.innerHTML = icon("layers", 13);
    el.title = "Baumdiagramm bearbeiten";
    el.setAttribute("aria-label", "Baumdiagramm bearbeiten");
    el.addEventListener("mousedown", (e) => e.preventDefault());
    el.addEventListener("click", (e) => {
      e.preventDefault();
      view.dispatch({ selection: { anchor: this.at } });
      this.run();
    });
    return el;
  }
  ignoreEvent(): boolean {
    return true;
  }
}

function treeChips(view: EditorView, run: () => void): DecorationSet {
  const positions = [view.state.selection.main.head];
  const hovered = view.state.field(hoverLineField, false);
  if (hovered != null) positions.push(hovered);
  const heads = new Set<number>();
  const text = view.state.doc.toString();
  for (const pos of positions) {
    // Billig vorab: nur Zeilen in der Nähe eines Mermaid-Zauns kommen in Frage.
    if (!text.includes("%% baum=")) break;
    const found = findTreeAt(text, pos);
    if (found) heads.add(found.start);
  }
  const b = new RangeSetBuilder<Decoration>();
  for (const at of [...heads].sort((x, y) => x - y)) {
    b.add(at, at, Decoration.widget({ widget: new TreeChip(at, run), side: -1 }));
  }
  return b.finish();
}

function treeChipPlugin(run: () => void) {
  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = treeChips(view, run);
      }
      update(u: ViewUpdate) {
        this.decorations = treeChips(u.view, run);
      }
    },
    { decorations: (v) => v.decorations }
  );
}

// ---------------------------------------------------------------------------
// "Skizze bearbeiten" am Bild-Quelltext – dieselbe Idee wie der Tabellen-Knopf.
// `run` findet die Skizze selbst über die Cursorposition (siehe insertDrawing
// in EditorPage.ts), der Chip muss also nur wissen, WANN er sichtbar ist.
// ---------------------------------------------------------------------------

const SVG_IMAGE_LINE_RE = /^\s*!\[[^\]\n]*\]\(pb:[^/\s]+\/[^)\s]+\.svg\)\s*$/i;

class SvgEditChip extends WidgetType {
  constructor(
    private readonly pos: number,
    private readonly run: () => void
  ) {
    super();
  }
  eq(other: SvgEditChip): boolean {
    return other.pos === this.pos;
  }
  toDOM(view: EditorView): HTMLElement {
    const el = document.createElement("button");
    el.type = "button";
    el.className = "cm-md-tableChip";
    el.textContent = "Skizze bearbeiten";
    el.title = "Zeichenfeld öffnen und diese Skizze weiterbearbeiten";
    el.addEventListener("mousedown", (e) => e.preventDefault());
    el.addEventListener("click", (e) => {
      e.preventDefault();
      // Bei reinem Hover (Maus über der Zeile, ohne dort auch den Cursor zu
      // haben) stand `run()` sonst noch an der alten Cursorposition und fand
      // dort keine Skizze – Ergebnis: leeres Zeichenfeld statt der Skizze.
      view.dispatch({ selection: { anchor: this.pos } });
      this.run();
    });
    return el;
  }
  ignoreEvent(): boolean {
    return true;
  }
}

function svgEditChips(view: EditorView, run: () => void): DecorationSet {
  const positions = [view.state.selection.main.head];
  const hovered = view.state.field(hoverLineField, false);
  if (hovered != null) positions.push(hovered);

  const lines = new Set<number>();
  for (const pos of positions) {
    const line = view.state.doc.lineAt(pos);
    if (SVG_IMAGE_LINE_RE.test(line.text)) lines.add(line.to);
  }

  const b = new RangeSetBuilder<Decoration>();
  for (const at of [...lines].sort((x, y) => x - y)) {
    b.add(at, at, Decoration.widget({ widget: new SvgEditChip(at, run), side: 1 }));
  }
  return b.finish();
}

function svgEditChipPlugin(run: () => void) {
  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = svgEditChips(view, run);
      }
      update(u: ViewUpdate) {
        this.decorations = svgEditChips(u.view, run);
      }
    },
    { decorations: (v) => v.decorations }
  );
}

// ---------------------------------------------------------------------------
// Faltdreiecke: eigene Widgets statt einer separaten Randspalte
// ---------------------------------------------------------------------------
//
// Eine echte CodeMirror-Gutter-Spalte (`foldGutter()`) reserviert eine feste,
// andersfarbige Leiste links – genau der „graue Rand", den es nicht mehr
// geben soll. Stattdessen sitzt das Dreieck als Widget INNERHALB der
// jeweiligen Zeile (an deren Anfang) und wird per CSS absolut nach links in
// den linken Innenabstand von `.cm-content` gelegt – exakt dieselbe Technik
// wie die Dreiecke in der Vorschau (siehe previewFold.ts). Dadurch bleibt es
// Teil der Textfläche (keine eigene Hintergrundfarbe, kein eigener
// Klickbereich über die ganze Spalte) und lässt sich rein über
// `.cm-line:hover` einblenden – keine Maus-Verfolgung mehr nötig.

class FoldMarkerWidget extends WidgetType {
  constructor(
    private readonly open: boolean,
    private readonly line0: number
  ) {
    super();
  }
  eq(other: FoldMarkerWidget): boolean {
    return other.open === this.open && other.line0 === this.line0;
  }
  toDOM(view: EditorView): HTMLElement {
    const el = document.createElement("span");
    el.className = "cm-md-foldMarker" + (this.open ? " is-open" : " is-closed");
    el.innerHTML = TRIANGLE;
    el.title = this.open ? "Zuklappen" : "Aufklappen";
    el.setAttribute("role", "button");
    // Ohne das verliert der Editor beim Klick den Fokus/die Cursorposition.
    el.addEventListener("mousedown", (e) => e.preventDefault());
    el.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      toggleFoldAt(view, this.line0);
    });
    return el;
  }
  ignoreEvent(): boolean {
    return true;
  }
}

function foldMarkerDecos(view: EditorView): DecorationSet {
  const { byLine } = docInfo(view.state);
  if (!byLine.size) return Decoration.none;
  const folded = new Set<number>();
  foldedRanges(view.state).between(0, view.state.doc.length, (from, to) => {
    if (to > from) folded.add(view.state.doc.lineAt(from).number - 1);
  });

  const b = new RangeSetBuilder<Decoration>();
  for (const line0 of [...byLine.keys()].sort((x, y) => x - y)) {
    const at = view.state.doc.line(line0 + 1).from;
    const open = !folded.has(line0);
    b.add(at, at, Decoration.widget({ widget: new FoldMarkerWidget(open, line0), side: -1 }));
  }
  return b.finish();
}

const foldMarkers = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = foldMarkerDecos(view);
    }
    update(u: ViewUpdate) {
      this.decorations = foldMarkerDecos(u.view);
    }
  },
  { decorations: (v) => v.decorations }
);

// ---------------------------------------------------------------------------

export interface CodeEditor extends MdField {
  readonly view: EditorView;
  /** Äußeres Element zum Einhängen. */
  readonly dom: HTMLElement;
  /** Das scrollbare Element (für das Mitscrollen mit der Vorschau). */
  readonly scroller: HTMLElement;
  /** Oberkante einer Quelltextzeile relativ zum Inhaltsanfang, in Pixeln. */
  lineTop(line0: number): number;
  /** Oberste sichtbare Zeile (0-basiert). */
  topLine(): number;
  /** Scrollt eine Zeile an den oberen Rand. */
  scrollToLine(line0: number): void;
  /** Faltbarer Bereich, der genau bei dieser Zeile beginnt (0-basierte Zeilen). */
  foldSpan(line0: number): FoldRange | null;
  /** Startzeilen aller gerade zugeklappten Bereiche. */
  foldedLines(): Set<number>;
  /** Zu-/Aufklappen des Bereichs, der bei `line0` beginnt. */
  toggleFold(line0: number): void;
  onFoldChange(fn: () => void): void;
  destroy(): void;
}

export interface CodeEditorOptions {
  value: string;
  placeholder?: string;
  /** Öffnet den visuellen Tabellen-Editor (Knopf am Tabellen-Quelltext). */
  onEditTable?: () => void;
  /**
   * Kennung für das Merken zugeklappter Bereiche über Reload/Neuöffnen hinweg
   * (siehe foldPersist.ts) – i. d. R. die Notiz-ID. Ohne Angabe wird nichts
   * gemerkt.
   */
  foldId?: string;
  /** Klick auf einen PDF-Verweis im Quelltext. */
  onOpenPdfRef?: (href: string) => void;
  /** "Skizze bearbeiten"-Knopf am Quelltext eines Skizzen-Bildes (.svg). */
  onEditSketch?: () => void;
  /** Randsymbol am Mermaid-Block eines Baumdiagramms. */
  onEditTree?: () => void;
}

/** Alle aktuell zugeklappten Bereiche als Merkposten fürs Wiederherstellen. */
function currentFoldMarks(state: EditorState): FoldMark[] {
  const marks: FoldMark[] = [];
  foldedRanges(state).between(0, state.doc.length, (from, to) => {
    if (to <= from) return;
    const line = state.doc.lineAt(from);
    marks.push({ line: line.number - 1, head: foldSnippet(line.text) });
  });
  return marks;
}

export function createCodeEditor(opts: CodeEditorOptions): CodeEditor {
  const listeners = new Map<string, ((e: any) => void)[]>();
  /**
   * Reihenfolge zählt: Wer das Ereignis per `preventDefault()` erledigt hat
   * (z. B. das Slash-Menü bei Enter), stoppt die folgenden Hörer – bei echten
   * DOM-Listenern täte das `stopImmediatePropagation()`, hier eben von Hand.
   */
  const emit = (type: string, e: Event) => {
    for (const fn of listeners.get(type) ?? []) {
      if (e.defaultPrevented) return;
      fn(e);
    }
  };
  const foldWatchers: (() => void)[] = [];
  let foldKey = "";

  /**
   * Eigene Tastatur-Handler laufen VOR den CodeMirror-Tastenkarten (`keys.ts`
   * führt Listen fort, rückt Überschriften ein …). Was dort per
   * `preventDefault()` erledigt wurde, sieht CodeMirror nicht mehr.
   */
  const ownKeys = Prec.highest(
    EditorView.domEventHandlers({
      keydown: (e) => {
        emit("keydown", e);
        return e.defaultPrevented;
      },
      // Mittlere Maustaste zieht eine Blockauswahl auf. Das `preventDefault`
      // gegen Autoscroll/Einfügen macht CodeMirror selbst, sobald die Auswahl
      // startet – hier darf es NICHT passieren, sonst überspringt CodeMirror
      // seine eigenen Maus-Handler (es bricht bei `defaultPrevented` ab) und
      // die Blockauswahl käme nie zustande.
      auxclick: (e) => {
        if ((e as MouseEvent).button === 1) e.preventDefault();
        return false;
      },
      paste: (e) => {
        emit("paste", e);
        return e.defaultPrevented;
      },
      // Safari/WebKit (Mac + iPad, auch als PWA) besitzt für contenteditable
      // eine EIGENE, native Undo-Verwaltung, die bei Cmd+Z/Cmd+Shift+Z manchmal
      // vor CodeMirrors eigenem `historyKeymap` zum Zug kommt – dann passiert
      // nichts, weil WebKit keinen sinnvollen nativen Undo-Schritt für den von
      // CodeMirror verwalteten Inhalt kennt. Bekannter CodeMirror-6-Kniff:
      // dieses `beforeinput`-Ereignis abfangen und stattdessen CodeMirrors
      // eigenen Undo/Redo-Befehl auslösen.
      beforeinput: (e, view) => {
        if (e.inputType === "historyUndo") {
          e.preventDefault();
          undo(view);
          return true;
        }
        if (e.inputType === "historyRedo") {
          e.preventDefault();
          redo(view);
          return true;
        }
        return false;
      },
    })
  );

  const extensions: Extension[] = [
    history(),
    drawSelection(),
    dropCursor(),
    EditorState.allowMultipleSelections.of(true),
    EditorView.lineWrapping,
    rectangularSelection({ eventFilter: (e) => e.button === 1 || (e.altKey && e.button === 0) }),
    crosshairCursor(),
    codeFolding({
      // Kein sichtbarer Platzhalter: die zugeklappte Zeile sieht im Quelltext
      // genauso aus wie in der Vorschau – blau hinterlegt, sonst unverändert.
      // Der Knoten bleibt trotzdem anklickbar zum Aufklappen.
      placeholderDOM: (_view, onclick) => {
        const el = document.createElement("span");
        el.className = "cm-md-foldPlaceholder";
        el.title = "Aufklappen";
        el.setAttribute("aria-label", "Aufklappen");
        el.addEventListener("click", onclick);
        return el;
      },
    }),
    foldMarkers,
    mdFoldService,
    foldGuards,
    foldedLineHighlight,
    emphasisStars,
    hoverLineField,
    hoverLineTracker,
    tableChipPlugin(() => opts.onEditTable?.()),
    svgEditChipPlugin(() => opts.onEditSketch?.()),
    treeChipPlugin(() => opts.onEditTree?.()),
    tableWidgets(() => opts.onEditTable?.()),
    tableAlignPlugin,
    pdfChipPlugin((href) => opts.onOpenPdfRef?.(href)),
    imageEyeToggle,
    markdownSearch(),
    // Vor `ownKeys`: Tab übernimmt dort einen Rechenvorschlag, bevor keys.ts einrückt.
    mathAssist(),
    ownKeys,
    keymap.of([...defaultKeymap, ...historyKeymap, ...foldKeymap]),
    EditorView.updateListener.of((u) => {
      if (u.docChanged) emit("input", new Event("input"));
      if (u.selectionSet && !u.docChanged) emit("select", new Event("select"));
      if (u.focusChanged && !u.view.hasFocus) emit("blur", new Event("blur"));
      const key = foldSignature(u.state);
      if (key !== foldKey) {
        foldKey = key;
        for (const fn of foldWatchers) fn();
      }
    }),
    placeholder(opts.placeholder ?? ""),
    // Im Theme statt im Stylesheet: so gewinnt es sicher gegen das
    // eingebaute Grundthema von CodeMirror (gepunkteter Fokusrahmen etc.).
    EditorView.theme({
      "&": { height: "100%" },
      "&.cm-focused": { outline: "none" },
      // Gleiche Schrift wie die Vorschau – damit sehen die Auszeichnungs-
      // Sternchen dort genauso aus wie im gerenderten Text.
      ".cm-scroller": { fontFamily: "var(--font)", fontSize: "14.5px", lineHeight: "1.7" },
      // Links nur so viel Platz, wie das Faltdreieck braucht (siehe
      // `.cm-md-foldMarker` im Stylesheet) – kein separater Rand, das Dreieck
      // liegt in genau diesem Innenabstand der Textfläche selbst.
      // `minWidth: 0` hebelt den Browser-Standard "Flex-Kind schrumpft nie
      // unter seinen Inhalt" aus: ohne das zwang ein einzelnes breites
      // Tabellen-Ausrichtungs-Widget (tableAlign.ts, feste Pixelbreite je
      // Zelle) den GESAMTEN Editor zum Querscrollen, sobald eine Spalte in
      // einer großen Tabelle stark unterschiedlich lange Zellen hatte – auch
      // wenn der restliche Text längst umgebrochen wäre.
      ".cm-content": { padding: "18px 20px 18px 18px", caretColor: "transparent", minWidth: "0" },
      // Positionierungs-Anker für das absolut gelegte Faltdreieck (siehe
      // FoldMarkerWidget) + kein eigener Innenabstand, der den Text nochmals
      // verschöbe.
      ".cm-line": { padding: "0", position: "relative" },
      // Cursor und Auswahl zeichnet CodeMirror selbst (drawSelection). Das
      // Grundthema ist auf Hell festgelegt – schwarzer Strich auf dunklem
      // Grund wäre unsichtbar, darum hier an die Themenfarben binden.
      ".cm-cursor, .cm-dropCursor": {
        borderLeftColor: "var(--text)",
        borderLeftWidth: "2px",
      },
      // Kein `&light`/`&dark` nötig: das sind Selektoren aus
      // `EditorView.baseTheme` (eigener Geltungsbereich), in `theme()` reicht
      // die normale Verschachtelung unter `&`.
      ".cm-selectionBackground": {
        background: "color-mix(in srgb, var(--primary) 22%, transparent)",
      },
      "&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground": {
        background: "color-mix(in srgb, var(--primary) 32%, transparent)",
      },
    }),
  ];

  const view = new EditorView({
    state: EditorState.create({ doc: opts.value, extensions }),
  });
  view.dom.classList.add("editor-code");
  // Der Editor entsteht, bevor die Seite im DOM hängt – zu diesem Zeitpunkt
  // misst CodeMirror ins Leere (Zeilenhöhe stimmt dann nicht, alles rutscht
  // beim ersten Klick zurecht). Der Beobachter meldet sich, sobald das
  // Element wirklich eine Größe hat, und danach bei jeder Größenänderung.
  const resize = new ResizeObserver(() => view.requestMeasure());
  resize.observe(view.dom);
  void document.fonts?.ready.then(() => view.requestMeasure());
  view.scrollDOM.addEventListener("scroll", (e) => emit("scroll", e), { passive: true });

  // Gemerkte Faltungen wiederherstellen (siehe foldPersist.ts) – nur Treffer,
  // bei denen Zeile UND Kopftext noch passen, sonst bliebe eine Faltung an
  // einer inzwischen ganz anderen Stelle hängen.
  if (opts.foldId) {
    const effects: StateEffect<{ from: number; to: number }>[] = [];
    for (const mark of loadFolds(opts.foldId)) {
      if (mark.line < 0 || mark.line >= view.state.doc.lines) continue;
      if (foldSnippet(view.state.doc.line(mark.line + 1).text) !== mark.head) continue;
      const range = charRange(view.state, mark.line);
      if (range) effects.push(foldEffect.of(range));
    }
    if (effects.length) view.dispatch({ effects });
    // Jede spätere Änderung merken – siehe `foldWatchers`-Aufruf im
    // `updateListener` oben.
    const id = opts.foldId;
    foldWatchers.push(() => saveFolds(id, currentFoldMarks(view.state)));
  }

  foldKey = foldSignature(view.state);

  /** Kompakte Kennung des Faltzustands – nur bei Änderung informieren. */
  function foldSignature(state: EditorState): string {
    const parts: string[] = [];
    foldedRanges(state).between(0, state.doc.length, (from, to) => {
      parts.push(from + ":" + to);
    });
    return parts.join(",");
  }

  const editor: CodeEditor = {
    view,
    dom: view.dom,
    scroller: view.scrollDOM,
    get value() {
      return view.state.doc.toString();
    },
    set value(next: string) {
      const cur = view.state.doc.toString();
      if (next === cur) return;
      // Nur den wirklich geänderten Mittelteil ersetzen: ein Rundumschlag über
      // das ganze Dokument würde jede Faltung verwerfen (z. B. wenn ein Haken
      // in der Vorschau ein einzelnes Zeichen umschreibt).
      let a = 0;
      while (a < cur.length && a < next.length && cur[a] === next[a]) a++;
      let b = 0;
      while (
        b < cur.length - a &&
        b < next.length - a &&
        cur[cur.length - 1 - b] === next[next.length - 1 - b]
      )
        b++;
      view.dispatch({
        changes: { from: a, to: cur.length - b, insert: next.slice(a, next.length - b) },
      });
    },
    get selectionStart() {
      return view.state.selection.main.from;
    },
    get selectionEnd() {
      return view.state.selection.main.to;
    },
    setSelectionRange(a, b) {
      const max = view.state.doc.length;
      const anchor = Math.max(0, Math.min(a, max));
      const head = Math.max(0, Math.min(b, max));
      view.dispatch({ selection: EditorSelection.single(anchor, head) });
    },
    focus: () => view.focus(),
    insert(text) {
      view.dispatch({
        ...view.state.replaceSelection(text),
        scrollIntoView: true,
        userEvent: "input.type",
      });
    },
    erase() {
      view.dispatch(
        view.state.changeByRange((r) => ({
          changes: { from: r.from, to: r.to },
          range: EditorSelection.cursor(r.from),
        }))
      );
    },
    on(type, fn) {
      const list = listeners.get(type);
      if (list) list.push(fn);
      else listeners.set(type, [fn]);
    },
    // CodeMirror meldet Änderungen selbst (updateListener oben) – ein
    // zusätzliches `input` würde die Vorschau doppelt neu bauen.
    emitInput() {},
    columns() {
      const cs = getComputedStyle(view.contentDOM);
      const inner =
        view.contentDOM.clientWidth -
        parseFloat(cs.paddingLeft || "0") -
        parseFloat(cs.paddingRight || "0");
      return Math.max(20, Math.floor(inner / charWidthOf(cs)));
    },
    /**
     * Einrücken über alle Auswahlbereiche – CodeMirror kann das selbst und
     * beherrscht dabei auch mehrere Cursor gleichzeitig. Bei genau einem
     * Bereich übernimmt weiterhin die Markdown-eigene Logik (Listen,
     * Überschriften), deshalb nur hier melden, was wirklich mehrfach ist.
     */
    /** Oberste sichtbare Zeile (0-basiert) – zum Merken des Scrollstands. */
    topLine() {
      const block = view.lineBlockAtHeight(view.scrollDOM.scrollTop);
      return view.state.doc.lineAt(block.from).number - 1;
    },
    /**
     * Zu einer Zeile scrollen. Über CodeMirrors eigenen Weg statt über
     * `scrollTop`: der Editor vermisst sich nach dem Einhängen neu und setzt
     * eine von Hand gesetzte Scrollposition dabei wieder zurück.
     */
    scrollToLine(line0) {
      const n = Math.max(1, Math.min(line0 + 1, view.state.doc.lines));
      const pos = view.state.doc.line(n).from;
      view.dispatch({ effects: EditorView.scrollIntoView(pos, { y: "start" }) });
    },
    indentRanges(outdent) {
      if (view.state.selection.ranges.length < 2) return false;
      (outdent ? indentLess : indentMore)({ state: view.state, dispatch: view.dispatch });
      return true;
    },
    caretCoords() {
      const c = view.coordsAtPos(view.state.selection.main.head);
      if (c) return { left: c.left, bottom: c.bottom };
      const r = view.dom.getBoundingClientRect();
      return { left: r.left + 20, bottom: r.top + 20 };
    },
    lineTop(line0) {
      const n = Math.max(1, Math.min(line0 + 1, view.state.doc.lines));
      return view.lineBlockAt(view.state.doc.line(n).from).top;
    },
    foldSpan: (line0) =>
      charRange(view.state, line0) ? docInfo(view.state).byLine.get(line0) ?? null : null,
    foldedLines() {
      const out = new Set<number>();
      foldedRanges(view.state).between(0, view.state.doc.length, (from, to) => {
        if (to > from) out.add(view.state.doc.lineAt(from).number - 1);
      });
      return out;
    },
    toggleFold: (line0) => toggleFoldAt(view, line0),
    onFoldChange(fn) {
      foldWatchers.push(fn);
    },
    destroy: () => {
      resize.disconnect();
      view.destroy();
    },
  };

  return editor;
}
